import { useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/Layout';
import { centralApi, DemoBanner } from '@/services/centralApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Bot, User, AlertTriangle, CheckCircle2, Send, Paperclip, MessageCircle, Search, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const FILTROS = [
  { key: 'all', label: 'Todas' },
  { key: 'ai', label: 'IA' },
  { key: 'human', label: 'Humano' },
  { key: 'waiting', label: 'Aguardando' },
  { key: 'unread', label: 'Não lidas' },
];

const modeIcon = (mode) => {
  if (mode === 'ai') return <Bot className="h-3.5 w-3.5 text-emerald-500" />;
  if (mode === 'human') return <User className="h-3.5 w-3.5 text-indigo-500" />;
  if (mode === 'waiting_human') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
  if (mode === 'closed') return <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />;
  return null;
};

const modeLabel = (mode) => ({
  ai: 'IA atendendo', human: 'Humano atendendo',
  waiting_human: 'Aguardando atendente', closed: 'Finalizada',
}[mode] || mode);

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatMoney = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CentralConversas() {
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState('all');
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [panel, setPanel] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [waConnected, setWaConnected] = useState(false);
  const scrollRef = useRef(null);
  const searchDebounce = useRef(null);

  useEffect(() => {
    centralApi.getWaSettings().then(s => setWaConnected(!!s?.connected)).catch(() => {});
  }, []);

  const reloadList = async (opts = {}) => {
    const { keepActive = true } = opts;
    const data = await centralApi.listConversations({
      filtro: filtro === 'all' ? undefined : filtro,
      search: search || undefined,
      limit: 60,
    });
    setItems(data.items || []);
    if (!keepActive && data.items?.length) setActiveId(data.items[0].id);
  };

  useEffect(() => { reloadList({ keepActive: false }); }, [filtro]);
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => reloadList(), 350);
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, [search]);

  useEffect(() => {
    if (!activeId) { setMessages([]); setPanel(null); setActiveConv(null); return; }
    (async () => {
      const [conv, m, p] = await Promise.all([
        centralApi.getConversation(activeId),
        centralApi.listMessages(activeId, { limit: 100 }),
        centralApi.getCustomerPanel(activeId),
      ]);
      setActiveConv(conv);
      setMessages(m.items || []);
      setPanel(p);
      centralApi.markRead(activeId).catch(() => {});
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e9 }));
    })();
  }, [activeId]);

  const send = async () => {
    if (!text.trim() || !activeId || sending) return;
    setSending(true);
    try {
      const msg = await centralApi.sendMessage(activeId, { sender_type: 'human', content: text.trim() });
      setMessages(prev => [...prev, msg]);
      setText('');
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e9 }));
      reloadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setSending(false);
    }
  };

  const doAction = async (fn, successMsg) => {
    try {
      const updatedConv = await fn(activeId);
      setActiveConv(updatedConv);
      const [m, p] = await Promise.all([
        centralApi.listMessages(activeId, { limit: 100 }),
        centralApi.getCustomerPanel(activeId),
      ]);
      setMessages(m.items || []);
      setPanel(p);
      await reloadList();
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro na ação');
    }
  };

  const canAssume = activeConv && ['ai', 'waiting_human'].includes(activeConv.attendance_mode);
  const canReturn = activeConv && activeConv.attendance_mode === 'human';
  const canClose = activeConv && activeConv.attendance_mode !== 'closed';

  return (
    <Layout>
    <div className="space-y-3" data-testid="central-conversas">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Conversas</h1>
          <p className="text-sm text-muted-foreground">Atendimento do WhatsApp integrado ao sistema Serraria Guedes</p>
        </div>
      </div>
      <DemoBanner when={!waConnected} />

      <div className="grid grid-cols-12 gap-3 min-h-[70vh]">
        {/* Coluna 1 — Lista */}
        <Card className="col-span-12 md:col-span-3 border-border flex flex-col overflow-hidden">
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Nome, telefone, CPF/CNPJ ou pedido"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="input-search-conversas"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {FILTROS.map(f => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={filtro === f.key ? 'default' : 'outline'}
                  className="h-7 text-xs px-2"
                  onClick={() => setFiltro(f.key)}
                  data-testid={`filtro-${f.key}`}
                >{f.label}</Button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Nenhuma conversa</div>
            ) : items.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${activeId === c.id ? 'bg-muted/60' : ''}`}
                data-testid={`conv-${c.id}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{c.customer_name || 'Novo contato'}</p>
                    <p className="text-xs text-muted-foreground truncate">+{c.phone}</p>
                    <p className="text-xs text-muted-foreground truncate mt-1">{c.last_message || '—'}</p>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <span className="text-[10px] text-muted-foreground">{timeAgo(c.last_message_at)}</span>
                    {c.unread_count > 0 && (
                      <Badge className="text-[10px] h-4 px-1 bg-amber-500">{c.unread_count}</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 text-[11px] text-muted-foreground">
                  {modeIcon(c.attendance_mode)} {modeLabel(c.attendance_mode)}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Coluna 2 — Chat */}
        <Card className="col-span-12 md:col-span-6 border-border flex flex-col overflow-hidden">
          {activeConv ? (
            <>
              <div className="p-3 border-b flex items-center justify-between">
                <div>
                  <p className="font-semibold">{activeConv.customer_name || 'Novo contato'}</p>
                  <p className="text-xs text-muted-foreground">+{activeConv.phone}</p>
                </div>
                <Badge variant="outline" className="text-xs">{modeLabel(activeConv.attendance_mode)}</Badge>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20">
                {messages.map(m => {
                  if (m.sender_type === 'system') {
                    return (
                      <div key={m.id} className="text-center text-[11px] text-muted-foreground italic py-1">
                        {m.content}
                      </div>
                    );
                  }
                  const isCustomer = m.sender_type === 'customer';
                  const bubbleColor = isCustomer
                    ? 'bg-background border border-border'
                    : m.sender_type === 'ai'
                      ? 'bg-emerald-500/15 border border-emerald-500/30'
                      : 'bg-primary/10 border border-primary/30';
                  return (
                    <div key={m.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${bubbleColor}`}>
                        {!isCustomer && (
                          <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-0.5 flex items-center gap-1">
                            {m.sender_type === 'ai' ? <><Bot className="h-3 w-3" />IA</> : <><User className="h-3 w-3" />{m.sender_name || 'Atendente'}</>}
                          </div>
                        )}
                        <p className="whitespace-pre-wrap break-words">{m.content}</p>
                        <p className="text-[10px] text-muted-foreground mt-1 text-right">{formatTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t p-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {canAssume && (
                    <Button size="sm" variant="outline" onClick={() => doAction(centralApi.assumeConversation, 'Atendimento assumido')} data-testid="btn-assume">
                      Assumir atendimento
                    </Button>
                  )}
                  {canReturn && (
                    <Button size="sm" variant="outline" onClick={() => doAction(centralApi.returnToAi, 'Devolvido para a IA')} data-testid="btn-return-ai">
                      Devolver para IA
                    </Button>
                  )}
                  {canClose && (
                    <Button size="sm" variant="outline" onClick={() => doAction(centralApi.closeConversation, 'Atendimento finalizado')} data-testid="btn-close">
                      Finalizar atendimento
                    </Button>
                  )}
                </div>
                {activeConv.attendance_mode === 'closed' ? (
                  <div className="text-xs text-muted-foreground italic text-center py-2">Conversa finalizada</div>
                ) : (
                  <div className="flex gap-2 items-end">
                    <Button variant="ghost" size="icon" className="shrink-0" disabled>
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <textarea
                      className="flex-1 rounded-md border border-input bg-background p-2 text-sm min-h-[42px] max-h-32 resize-y"
                      placeholder="Digite uma mensagem…"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                      }}
                      data-testid="input-message"
                    />
                    <Button onClick={send} disabled={!text.trim() || sending} data-testid="btn-send">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              <div className="text-center space-y-2">
                <MessageCircle className="h-8 w-8 mx-auto opacity-40" />
                Selecione uma conversa
              </div>
            </div>
          )}
        </Card>

        {/* Coluna 3 — Cliente */}
        <Card className="col-span-12 md:col-span-3 border-border overflow-y-auto">
          {!activeConv ? (
            <CardContent className="p-4 text-sm text-muted-foreground">Sem conversa selecionada</CardContent>
          ) : (
            <CardContent className="p-4 space-y-4 text-sm">
              <div>
                <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Cliente</p>
                {panel?.customer ? (
                  <div className="space-y-1">
                    <p className="font-medium">{panel.customer.nome}</p>
                    <p className="text-xs text-muted-foreground">+{panel.customer.telefone || activeConv.phone}</p>
                    {panel.customer.cpf_cnpj && <p className="text-xs text-muted-foreground">{panel.customer.cpf_cnpj}</p>}
                    {panel.customer.endereco && <p className="text-xs text-muted-foreground">{panel.customer.endereco}</p>}
                    <Link to={`/customers?open=${panel.customer.id}`}>
                      <Button size="sm" variant="outline" className="w-full mt-2 h-7 text-xs">
                        <ExternalLink className="h-3 w-3 mr-1" /> Abrir cadastro
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    Nenhum cliente encontrado com este telefone (+{activeConv.phone}).
                  </div>
                )}
              </div>

              {panel?.customer && (
                <div>
                  <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Pedidos</p>
                  {panel.pedidos?.length ? (
                    <div className="space-y-2">
                      {panel.pedidos.map(p => (
                        <Link key={p.id} to={`/orders?open=${p.id}`} className="block border border-border rounded-md p-2 hover:bg-muted/40">
                          <div className="flex justify-between text-xs">
                            <span className="font-mono font-medium">{p.numero_pedido}</span>
                            <span className="text-muted-foreground">{p.data_pedido ? new Date(p.data_pedido).toLocaleDateString('pt-BR') : '—'}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground">Status: {p.status}</div>
                          <div className="text-[11px] font-mono">{formatMoney(p.total_com_desconto || p.total_venda)}</div>
                        </Link>
                      ))}
                      <Link to={`/orders?cliente=${panel.customer.id}`}>
                        <Button size="sm" variant="ghost" className="w-full h-7 text-xs">Ver todos os pedidos</Button>
                      </Link>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Nenhum pedido registrado.</p>
                  )}
                </div>
              )}

              {panel?.customer && (
                <div>
                  <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Situação atual</p>
                  <div className="space-y-2">
                    <div className="border border-border rounded-md p-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Orçamento</p>
                      {panel.situacao?.orcamento ? (
                        <>
                          <p className="text-xs font-medium">{panel.situacao.orcamento.numero || panel.situacao.orcamento.id}</p>
                          <p className="text-[11px] text-muted-foreground">Status: {panel.situacao.orcamento.status}</p>
                        </>
                      ) : <p className="text-[11px] text-muted-foreground">Nenhum orçamento em aberto.</p>}
                    </div>
                    <div className="border border-border rounded-md p-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Produção</p>
                      {panel.situacao?.producao ? (
                        <>
                          <p className="text-xs font-medium">{panel.situacao.producao.numero_os || panel.situacao.producao.id}</p>
                          <p className="text-[11px] text-muted-foreground">Etapa: {panel.situacao.producao.etapa_atual || panel.situacao.producao.status || '—'}</p>
                        </>
                      ) : <p className="text-[11px] text-muted-foreground">Sem produção ativa.</p>}
                    </div>
                    <div className="border border-border rounded-md p-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Entrega</p>
                      {panel.situacao?.entrega ? (
                        <>
                          <p className="text-xs">Status: {panel.situacao.entrega.status}</p>
                          {panel.situacao.entrega.romaneio_numero && <p className="text-[11px] text-muted-foreground">Romaneio: {panel.situacao.entrega.romaneio_numero}</p>}
                        </>
                      ) : <p className="text-[11px] text-muted-foreground">Entrega ainda não programada.</p>}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </div>
    </Layout>
  );
}
